'use client';

import { useCallback, useMemo, useState } from 'react';
import { Box, Grid } from '@mui/material';
import { useTranslations } from 'next-intl';

import { fetchProjects } from '@dh/api/projects';
import {
    ContentHeader,
    LoadingProjectCard,
    NoEntries,
    ProjectCard,
    TopicFilterSort
} from '@dh/components';
import { ContentWidthWrapper } from '@dh/components/content-width-wrapper';
import { useBreakpoint, useTheme, useTopicCluster, useUpdateEffect } from '@dh/hooks';
import { getBreakpointValue, isCompact, Project, ProjectPopulation, Topic } from '@dh/types';
import { SortBy } from '@dh/utils';

import { ProjectContainer } from './styles';

interface Props {
    initialProjects: Project[];
    allTopics: Topic[];
}

export const ProjectOverviewScreen = ({ initialProjects, allTopics }: Props) => {
    const breakpoint = useBreakpoint();
    const theme = useTheme();
    const t = useTranslations('projectOverview');

    // Use topic cluster hook
    const { filteredTopics, topicOptions, handleTopicFilterChange } = useTopicCluster(
        allTopics,
        theme
    );

    const [projects, setProjects] = useState(initialProjects);
    const [isLoading, setIsLoading] = useState(false);
    const [sortByValue, setSortByValue] = useState(SortBy.Newest);
    const isEmpty = !isLoading && projects.length === 0;

    const refreshProjects = useCallback(
        async ({
            withTopics,
            sortBy
        }: {
            withTopics: Topic[];
            sortBy: SortBy;
        }) => {
            setIsLoading(true);
            const { projects: filteredProjects } = await fetchProjects({
                filters: {
                    topics: withTopics.map(({ id }) => id)
                },
                sort: sortBy === SortBy.Newest ? ['createdAt:desc'] : ['createdAt:asc'],
                populateVariant: ProjectPopulation.Full
            });
            setProjects(filteredProjects);
            setIsLoading(false);
        },
        []
    );
    useUpdateEffect(() => {
        void refreshProjects({ withTopics: filteredTopics, sortBy: sortByValue });
    }, [filteredTopics, sortByValue, refreshProjects]);

    // Responsive grid item widths for consistent layout across all grid screens
    // Compact (mobile): 100% - single column
    // Medium (tablet): 48.5% - two columns (slightly under 50% to account for gap)
    // Expanded (desktop): 360px - fixed optimal card width
    const gridItemStyle = useMemo(
        () => ({
            display: 'flex',
            width: getBreakpointValue(breakpoint, '100%', '48.5%', '360px')
        }),
        [breakpoint]
    );

    const renderProjects = useMemo(
        () => (
            <ProjectContainer container>
                {isLoading
                    ? Array.from({ length: 6 }).map((_, i) => <LoadingProjectCard key={i} />)
                    : projects.map((project) => (
                        <Grid key={project.documentId} style={gridItemStyle}>
                            <ProjectCard project={project} />
                        </Grid>
                    ))}
            </ProjectContainer>
        ),
        [isLoading, projects, gridItemStyle]
    );

    return (
        <ContentWidthWrapper variant='wide'>
            <Box>
                <ContentHeader
                    title={t('overview_title')}
                    description={t('overview_description')}
                    mx={isCompact(breakpoint) ? 2 : 3}
                    mt={5}
                    mb={5}
                />

                <Box mx={3}>
                    <TopicFilterSort
                        options={topicOptions}
                        filterChangeAction={handleTopicFilterChange}
                        sortBy={sortByValue}
                        updateSortByAction={setSortByValue}
                        disabled={isLoading}
                        translationNamespace='projectOverview'
                    />
                </Box>
                {isEmpty ? <NoEntries /> : renderProjects}
            </Box>
        </ContentWidthWrapper>
    );
};
